import axios from 'axios';

// API Response types
export interface ApiResult<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface ApiError {
  message: string;
  status?: number;
  details?: any;
}

// Resolve base URL with robust fallbacks:
// 1) REACT_APP_API_URL if set and appropriate for current host
// 2) window.__CONFIG__.API_URL (optional runtime config via /config.js)
// 3) If localhost -> default to API dev port 5280 under /api
// 4) Otherwise -> use relative "/api" and rely on reverse proxy in production
const resolveBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;
  const w = typeof window !== 'undefined' ? window as any : undefined;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';

  // If env URL exists but points to localhost and we're not on localhost, ignore it
  if (envUrl && !(envUrl.includes('localhost') && !isLocalHost)) {
    return envUrl;
  }

  const runtimeUrl = w?.__CONFIG__?.API_URL as string | undefined;
  if (runtimeUrl) return runtimeUrl;

  if (isLocalHost) {
    // dev default: full URL with /api
    return 'http://localhost:5280/api';
  }

  // production default (behind Nginx proxy)
  // Return empty baseURL so endpoint paths like '/api/...'
  // resolve relative to the current origin without duplicating '/api'.
  return '';
};

export const api = axios.create({
  baseURL: resolveBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const apiError: ApiError = {
      message: 'Ocorreu um erro inesperado',
      status: error.response?.status,
      details: error.response?.data,
    };

    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 400:
          apiError.message = 'Pedido inválido. Verifique os dados.';
          break;
        case 401:
          apiError.message = 'Sessão terminada. Entre de novo.';
          // Clear invalid token and send the user back to sign in
          localStorage.removeItem('token');
          if (window.location.pathname !== '/login') window.location.assign('/login');
          window.location.href = '/login';
          break;
        case 403:
          // A refusal, not a bad token: keep the session and show the server's reason
          apiError.message = (error.response.data as any)?.message || 'Não tem permissão para fazer isto.';
          break;
        case 404:
          apiError.message = 'Não encontrado.';
          break;
        case 500:
          apiError.message = 'Erro no servidor. Tente mais tarde.';
          break;
        default:
          // Try to extract message from ApiResult response
          if (error.response.data && typeof error.response.data === 'object') {
            const responseData = error.response.data as any;
            if (responseData.message) {
              apiError.message = responseData.message;
            }
          }
      }
    } else if (error.request) {
      // Request was made but no response received
      apiError.message = 'Sem ligação ao servidor. Verifique a rede e tente de novo.';
    } else {
      // Something else happened
      apiError.message = error.message || 'Ocorreu um erro inesperado';
    }

    return Promise.reject(apiError);
  }
);

// Helper function to handle API calls with consistent error handling
export const handleApiCall = async <T>(
  apiCall: () => any
): Promise<T> => {
  try {
    const response = await apiCall();
    const result = response.data;

    if (!result.success) {
      throw new Error(result.message || 'API call failed');
    }

    return result.data as T;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    // The response interceptor rejects with an ApiError: prefer the ApiResult message the server sent
    const apiError = error as ApiError | undefined;
    const serverMessage = apiError?.details?.message;
    if (typeof serverMessage === 'string' && serverMessage) {
      throw new Error(serverMessage);
    }
    throw new Error(apiError?.message || 'Ocorreu um erro inesperado');
  }
};
